import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { round } from 'lodash';

import {
    RELEASE_SYNC as baseVariant,
    newQuickJSWASMModuleFromVariant,
    newVariant,
    QuickJSContext,
    QuickJSHandle,
    QuickJSRuntime,
    QuickJSWASMModule,
    VmFunctionImplementation,
} from 'quickjs-emscripten';

import { WorkOrdersActions } from '../job/job-workorders/job-workorders.actions';

import { EditableCharge } from '../job/job-workorders/job-workorders.state';

import { quickJsActions } from './quickjs.actions';

declare global {

    interface SymbolConstructor {
        readonly dispose: symbol;
    }

    interface Disposable {
        dispose(): void;
    }

    // eslint-disable-next-line no-var
    var QuickJS: QuickJSWASMModule;
    // eslint-disable-next-line no-var
    var QuickJSPromise: Promise<QuickJSWASMModule>;
}

export class RetriableError extends Error {
    readonly retriable = true;

    constructor(
        message: string,
        public readonly cause?: unknown,
    ) {
        super(message);
        this.name = 'RetriableError';
    }
}

const ONE_KILOBYTE = 1024;
const ONE_MEGABYTE = ONE_KILOBYTE * ONE_KILOBYTE;
const MAX_INSTRUCTIONS = 250;
const TIMEOUT = 1000;

@Injectable({
    providedIn: 'root',
})
export class QuickJsService {

    constructor(
        private store: Store,
    ) {}

    private _runtime: QuickJSRuntime;

    /** Context used by the calculator script. */
    private _calculatorContext: QuickJSContext;

    /** Separate context used by navigator scripts — never reset by calculator reloads. */
    private _navigatorContext: QuickJSContext;

    private _calculatorQueue: Promise<unknown> = Promise.resolve();

    private _interrupt = {
        lastReset: Date.now(),
        instructions: 0,
    }

    // ─── QuickJS module / runtime ────────────────────────────────────────────

    public getQuickJsModule() {
        if (globalThis.QuickJS) return Promise.resolve(globalThis.QuickJS);
        if (globalThis.QuickJSPromise) return globalThis.QuickJSPromise;

        const variant = newVariant(baseVariant, {
            // Ignore map file as it doesn't work with Angular
            locateFile: (filename) => filename.endsWith('.wasm.map') ? '' : `/assets/wasm/${filename}`,
        });

        globalThis.QuickJSPromise = newQuickJSWASMModuleFromVariant(variant)
            .then((mod) => (globalThis.QuickJS = mod, mod))
            .catch((err) => {
                delete globalThis.QuickJSPromise;
                throw err;
            });

        return globalThis.QuickJSPromise;
    }

    private async _ensureRuntime() {

        if (this._runtime) return this._runtime;

        const qjsModule = await this.getQuickJsModule();
        const runtime = qjsModule.newRuntime();

        runtime.setMemoryLimit(ONE_MEGABYTE);
        runtime.setInterruptHandler(() => {

            if (this._interrupt.instructions > MAX_INSTRUCTIONS) {
                return true;
            }

            if (Date.now() > this._interrupt.lastReset + TIMEOUT) {
                return true;
            }

            this._interrupt.instructions++;

            return false;
        });

        this._runtime = runtime;

        return this._runtime;
    }

    // ─── Context helpers (context-parameterised) ─────────────────────────────

    private _loadGlobalFunctionOnContext(
        ctx: QuickJSContext,
        name: string,
        fn: VmFunctionImplementation<QuickJSHandle>,
    ) {
        const funcHandle = ctx.newFunction(name, fn);
        ctx.setProp(ctx.global, name, funcHandle);
        funcHandle.dispose();
    }

    private _convertToQuickJSObjectOnContext(ctx: QuickJSContext, data: any): QuickJSHandle {

        if (typeof data === 'string') {
            return ctx.newString(data);
        }

        if (typeof data === 'number') {
            return ctx.newNumber(data);
        }

        if (typeof data === 'boolean') {
            return data ? ctx.true : ctx.false;
        }

        if (Array.isArray(data)) {

            const arrayObj = ctx.newArray();

            data.forEach((item, index) => {
                const temp = this._convertToQuickJSObjectOnContext(ctx, item);
                ctx.setProp(arrayObj, index, temp);
                temp.dispose();
            });
            return arrayObj;
        }

        if (data && typeof data === 'object') {

            const obj = ctx.newObject();

            for (const [key, value] of Object.entries(data)) {
                const temp = this._convertToQuickJSObjectOnContext(ctx, value);
                ctx.setProp(obj, key, temp);
                temp.dispose();
            }

            return obj;
        }

        return ctx.undefined;
    }

    private _hasGlobalFunctionOn(ctx: QuickJSContext, funcName: string): boolean {
        const handle = ctx.getProp(ctx.global, funcName);
        const isFunction = ctx.typeof(handle) === 'function';
        handle.dispose();
        return isFunction;
    }

    private _runInContextOn<Output = any>(
        ctx: QuickJSContext,
        funcName: string,
        args?: any[],
        opts?: { isReset?: boolean; },
    ) {

        // `getProp` always returns a handle — a missing global yields a handle to `undefined`,
        // not a falsy value — so the old `if (!runFunction)` guard could never fire and its
        // `ctx.dispose()` was unreachable. Check the type instead, and leave the context alone:
        // `_calculatorContext` / `_navigatorContext` are long-lived fields owned solely by the
        // reset methods, and disposing one here left the field pointing at a dead lifetime.
        if (!this._hasGlobalFunctionOn(ctx, funcName)) {
            console.error(`Function '${funcName}' not found in script`);
            throw new Error(`Function '${funcName}' not found`);
        }

        const runFunction = ctx.getProp(ctx.global, funcName);

        const argHandles = (args ?? []).map((arg) => this._convertToQuickJSObjectOnContext(ctx, arg));

        this._resetInterrupt();

        const result = ctx.callFunction(runFunction, ctx.undefined, argHandles);

        runFunction.dispose();
        argHandles.forEach((h) => h.dispose());

        if (result.error) {

            const msgHandle = ctx.getProp(result.error, 'message');
            const originalMessage: string = ctx.dump(msgHandle);
            msgHandle.dispose();

            const stackHandle = ctx.getProp(result.error, 'stack');
            const stack = ctx.dump(stackHandle);
            stackHandle.dispose();

            result.error.dispose();

            const message = `${funcName} execution failed: ${originalMessage}`;

            if (opts?.isReset) {
                const nonRetriable = new Error(message);
                nonRetriable.stack = stack;
                throw nonRetriable;
            }

            const retriable = new RetriableError(message);
            throw retriable;
        }

        const value = result.unwrap();
        const output = ctx.dump(value);
        console.log('Success:', output);
        value.dispose();

        return output as Output;
    }

    private _evalScriptOnContext(ctx: QuickJSContext, script: string) {

        this._resetInterrupt();

        const codeResult = ctx.evalCode(script);

        if (!codeResult.error) {
            ctx.unwrapResult(codeResult).dispose();
            return;
        }

        const error = ctx.dump(codeResult.error);
        console.error('Script parsing failed:', error);
        codeResult.error.dispose();
        // Deliberately does NOT dispose the context. It belongs to `_resetCalculatorContext` /
        // `_resetNavigatorContext`, which are the only things that create and destroy it. Disposing
        // here left `_calculatorContext` / `_navigatorContext` pointing at a dead lifetime, so the
        // *next* load — which begins by disposing the old context — threw instead of recovering.
        // One script parse error therefore bricked script loading until a page reload.
        throw new Error('Script parsing error');
    }

    // ─── Shared base globals (used by both calculator and navigator) ──────────

    private _loadBaseGlobals(ctx: QuickJSContext) {

        this._loadGlobalFunctionOnContext(ctx, 'log', (...args) => {
            const nativeArgs = args.map((arg) => ctx.dump(arg));
            console.log(...nativeArgs);
        });

        this._loadGlobalFunctionOnContext(ctx, 'round', (...args) => {
            const nativeArgs = args.map((arg) => ctx.dump(arg));
            const num = round(nativeArgs[0], nativeArgs[1]);
            return ctx.newNumber(num);
        });

        this._loadGlobalFunctionOnContext(ctx, 'getFloat', (...args) => {
            const nativeArgs = args.map((arg) => ctx.dump(arg));
            const value = nativeArgs[0];
            const defaultValue = nativeArgs[1] ?? 0;
            const num = parseFloat(value);
            return ctx.newNumber(isNaN(num) ? defaultValue : num);
        });

        this._loadGlobalFunctionOnContext(ctx, 'getInt', (...args) => {
            const nativeArgs = args.map((arg) => ctx.dump(arg));
            const value = nativeArgs[0];
            const defaultValue = nativeArgs[1] ?? 0;
            const num = parseInt(value, 10);
            return ctx.newNumber(isNaN(num) ? defaultValue : num);
        });

        this._loadGlobalFunctionOnContext(ctx, 'createEvent', (inputHandle) => {
            const input = ctx.dump(inputHandle);
            const eventId = crypto.randomUUID();
            this.store.dispatch(WorkOrdersActions.eventTypeMenuItemSelected({
                eventType: input.type,
                eventId,
            }));
            return this._convertToQuickJSObjectOnContext(ctx, { id: eventId });
        });

        this._loadGlobalFunctionOnContext(ctx, 'addCharge', (eventIdHandle, inputHandle) => {
            const eventId = ctx.dump(eventIdHandle);
            const charge: EditableCharge = ctx.dump(inputHandle);
            this.store.dispatch(quickJsActions.addChargeRequested({ eventId, charge }));
            return this._convertToQuickJSObjectOnContext(ctx, { charge });
        });
    }

    // ─── Navigator-specific globals (JSX) ────────────────────────────────────

    private _loadNavigatorBindings(ctx: QuickJSContext) {

        this._loadGlobalFunctionOnContext(ctx, 'exit', () => {
            // no-op stub
        });

        this._loadGlobalFunctionOnContext(ctx, 'openURL', (urlHandle) => {
            console.log('openURL called with:', ctx.dump(urlHandle));
        });

        const tagNames: Record<string, string> = {
            // Structural
            EventNavigator: 'EventNavigator',
            Step: 'Step',

            // Content / info
            CustomerInfo: 'CustomerInfo',
            LocationInfo: 'LocationInfo',
            JobNotes: 'JobNotes',
            CrewNotes: 'JobNotes',   // alias — both map to the same component

            // Inputs / interaction
            Checkbox: 'Checkbox',
            Button: 'Button',
            InventoryEditor: 'InventoryEditor',
            InventorySummary: 'InventorySummary',
            CustomerSignature: 'CustomerSignature',

            // New navigator components (v2)
            CrewRollCall: 'CrewRollCall',
            PhotoCapture: 'PhotoCapture',
            HomeProtection: 'HomeProtection',
            StarRating: 'StarRating',
            MovePerformanceSummary: 'MovePerformanceSummary',
            PandaDocSigner: 'PandaDocSigner',
            LocationPermission: 'LocationPermission',
            LoadProgress: 'LoadProgress',
            CollectPayment: 'CollectPayment',

            // Primitives
            P: 'P',
        };
        for (const [name, value] of Object.entries(tagNames)) {
            const handle = ctx.newString(value);
            ctx.setProp(ctx.global, name, handle);
            handle.dispose();
        }

        // Fragment sentinel as a string (Symbols can't cross the QuickJS/host boundary)
        const fragmentHandle = ctx.newString('__Fragment__');
        ctx.setProp(ctx.global, 'Fragment', fragmentHandle);
        fragmentHandle.dispose();

        this._loadGlobalFunctionOnContext(ctx, 'h', (typeHandle, propsHandle, ...childHandles) => {
            const type = ctx.dump(typeHandle);
            const rawProps = propsHandle ? ctx.dump(propsHandle) : {};

            // Strip Sucrase debug props — __self captures the entire global scope
            const { __self: _s, __source: _src, ...props } = rawProps ?? {};

            const children = childHandles
                .map((c) => ctx.dump(c))
                .flat()
                .filter((c) => c !== null && c !== undefined);

            if (type === '__Fragment__') {
                return this._convertToQuickJSObjectOnContext(ctx, children);
            }

            return this._convertToQuickJSObjectOnContext(ctx, { type, props, children });
        });
    }

    // ─── Context lifecycle ────────────────────────────────────────────────────

    private async _freshContext(): Promise<QuickJSContext> {
        const runtime = await this._ensureRuntime();
        return runtime.newContext();
    }

    private async _resetCalculatorContext() {
        // `alive` guard: belt and braces alongside the two call sites that no longer dispose a
        // context they do not own. Disposing an already-disposed lifetime throws, and this is the
        // recovery path — it must never be the thing that fails.
        if (this._calculatorContext) {
            if (this._calculatorContext.alive) {
                this._calculatorContext.dispose();
            }
            this._calculatorContext = undefined;
        }
        const ctx = await this._freshContext();
        this._loadBaseGlobals(ctx);
        this._calculatorContext = ctx;
    }

    private async _resetNavigatorContext() {
        // See `_resetCalculatorContext` — same reasoning.
        if (this._navigatorContext) {
            if (this._navigatorContext.alive) {
                this._navigatorContext.dispose();
            }
            this._navigatorContext = undefined;
        }
        const ctx = await this._freshContext();
        this._loadBaseGlobals(ctx);
        this._loadNavigatorBindings(ctx);
        this._navigatorContext = ctx;
    }

    private _enqueueCalculatorTask<T>(task: () => Promise<T>): Promise<T> {
        const run = this._calculatorQueue.then(task);
        this._calculatorQueue = run.catch(() => undefined);
        return run;
    }

    // ─── Interrupt ───────────────────────────────────────────────────────────

    private _resetInterrupt() {
        this._interrupt = {
            lastReset: Date.now(),
            instructions: 0,
        }
    }

    // ─── Public API ──────────────────────────────────────────────────────────

    /** Load a calculator script. Resets only the calculator context. */
    public loadScript(script: string) {
        return this._enqueueCalculatorTask(async () => {
            await this._resetCalculatorContext();
            this._evalScriptOnContext(this._calculatorContext, script);
        });
    }

    /** Load a navigator script. Resets only the navigator context. */
    public async loadNavigatorScript(script: string) {
        await this._resetNavigatorContext();
        this._evalScriptOnContext(this._navigatorContext, script);
    }

    public unloadScript() {
        return this._enqueueCalculatorTask(() => this._resetCalculatorContext());
    }

    public runCalculations(
        input: CalculateStateParams,
        opts?: { isReset?: boolean },
    ) {
        const calculatedState = this._runInContextOn(this._calculatorContext, 'calculateState', [input], opts);

        const elements = this._runInContextOn(
            this._calculatorContext,
            'getElements',
            [{ input, state: calculatedState }],
            opts,
        );

        const customerElements: FormElement[] = this._hasGlobalFunctionOn(this._calculatorContext, 'getCustomerElements')
            ? this._runInContextOn(
                this._calculatorContext,
                'getCustomerElements',
                [{ input, state: calculatedState }],
                opts,
            )
            : [];

        return { calculatedState, elements, customerElements };
    }

    public runButtonAction(functionName: string, input: any) {
        return this._runInContextOn(this._calculatorContext, functionName, [input]);
    }

    public getNavigatorTree(input: any, state: any, opts?: { isReset?: boolean }) {
        return this._runInContextOn(this._navigatorContext, 'getSteps', [input, state], opts);
    }

    public reloadScript() {
        this.store.dispatch(quickJsActions.reloadScriptCommandEntered());
    }

    public setScript(url: string) {
        this.store.dispatch(quickJsActions.scriptUrlEnteredForJob({ url }));
    }
}
